import { supabase } from '@/integrations/supabase/client';
import { invokeWithRetry } from './common/invokeWithRetry';
import i18n from "@/i18n";

// Feature flags for bookings RPC edge function actions
const FEATURE_FLAGS = {
  CANCEL_BOOKING_WITH_JOKER: true,
  BOOK_CLASS_SLOT: true,
  GET_ALL_CLASS_AVAILABILITIES: true,
};

const BOOKING_BUSINESS_ERROR_CODES = [
  'NO_VALID_SUBSCRIPTION_FOR_DATE',
  'NO_SESSIONS_AVAILABLE',
  'CALENDAR_NOT_ALLOWED'
];

function throwBookingBusinessError(code: string): never {
  const e = new Error(code) as Error & { code: string };
  e.code = code;
  throw e;
}

/**
 * Code d'erreur émis quand `invokeWithRetry` signale une session expirée.
 * Pourquoi lever plutôt que renvoyer une valeur « neutre » : un `return false`
 * est indiscernable d'un refus métier côté appelant, et le flux de réservation
 * enchaînait alors journalisation, e-mail et toast de succès pour une
 * réservation qui n'a jamais existé (audit 3.1).
 */
export const AUTH_EXPIRED_ERROR_CODE = 'AUTH_EXPIRED';

export function isAuthExpiredError(error: unknown): boolean {
  return (error as { code?: string })?.code === AUTH_EXPIRED_ERROR_CODE;
}

function throwAuthExpiredError(action: string): never {
  const e = new Error(
    i18n.t("common:errors.sessionExpiredDuringAction", { action })
  ) as Error & { code: string };
  e.code = AUTH_EXPIRED_ERROR_CODE;
  throw e;
}

function findBookingBusinessErrorCode(...values: unknown[]): string | undefined {
  return BOOKING_BUSINESS_ERROR_CODES.find((code) =>
    values.some((value) => String(value || '').includes(code))
  );
}

async function readFunctionErrorBody(error: unknown): Promise<string> {
  const context = (error as { context?: unknown })?.context;
  if (!context) return '';
  if (typeof context === 'string') return context;
  if (context instanceof Response) {
    try {
      return await context.clone().text();
    } catch {
      return '';
    }
  }
  const body = (context as { body?: unknown; responseText?: unknown })?.body;
  const responseText = (context as { body?: unknown; responseText?: unknown })?.responseText;
  return String(body || responseText || '');
}

export interface BookClassSlotParams {
  classId: string;
  classDate: string;
  time: string;
  duration: number;
  userId?: string; // ID du client pour qui on réserve
}

interface BookClassSlotResponse {
  success?: boolean;
  error?: string;
  code?: string;
}

export interface CancelBookingWithJokerResult {
  success: boolean;
  data: {
    success: boolean;
    class_info: any;
    needs_joker: boolean;
    joker_used: boolean;
    session_used: boolean;
  };
}

export interface ClassAvailability {
  class_id: string;
  class_name: string;
  class_date: string;
  class_time: string;
  instructor: string;
  category_id: string;
  category: string;
  capacity: number;
  duration: string;
  booked: number;
  available: number;
}

/**
 * Cancel booking with automatic joker management
 */
export async function cancelBookingWithJoker(
  bookingId: string,
  gymId: string
): Promise<CancelBookingWithJokerResult> {
  if (FEATURE_FLAGS.CANCEL_BOOKING_WITH_JOKER) {
    // Use edge function
    const { data, error, authExpired } = await invokeWithRetry('bookings-management', {
      action: 'cancel_booking_with_joker',
      gym_id: gymId,
      bookingId,
    });

    if (authExpired) {
      return { success: false, data: null as any };
    }

    if (error) throw error;
    return data;
  } else {
    // Fallback to direct RPC call
    const { data, error } = await supabase.rpc('cancel_booking_with_joker', {
      p_booking_id: bookingId,
      p_gym_id: gymId,
    });

    if (error) throw error;
    return { success: true, data: data as any };
  }
}

/**
 * Book a class slot with capacity and duplicate checks
 */
export async function bookClassSlot(
  params: BookClassSlotParams,
  gymId: string
): Promise<boolean> {
  if (FEATURE_FLAGS.BOOK_CLASS_SLOT) {
    let invokeResult: Awaited<ReturnType<typeof invokeWithRetry<BookClassSlotResponse>>>;
    try {
      invokeResult = await invokeWithRetry<BookClassSlotResponse>('bookings-management', {
        action: 'book_class_slot',
        gym_id: gymId,
        userId: params.userId,
        ...params,
      });
    } catch (invokeError) {
      const msg = (invokeError as { message?: string })?.message || String(invokeError);
      const body = await readFunctionErrorBody(invokeError);
      const businessCode = findBookingBusinessErrorCode(msg, body);
      if (businessCode) throwBookingBusinessError(businessCode);
      throw invokeError;
    }

    const { data, error, authExpired } = invokeResult;

    // Session expirée : aucune ligne n'a été écrite. On lève pour que
    // l'appelant emprunte la branche d'échec (pas de log BOOKING_CREATED,
    // pas d'e-mail, pas de toast de succès).
    if (authExpired) throwAuthExpiredError('book_class_slot');

    if (error) {
      const msg = (error as { message?: string })?.message || String(error);
      const ctx = (error as { context?: { body?: string; responseText?: string } })?.context;
      const ctxBody = ctx?.body || ctx?.responseText || '';
      const businessCode = findBookingBusinessErrorCode(msg, ctxBody);
      if (businessCode) throwBookingBusinessError(businessCode);
      throw error;
    }
    const businessCode = findBookingBusinessErrorCode(data?.code, data?.error);
    if (businessCode) throwBookingBusinessError(businessCode);
    return data?.success === true;
  } else {
    const { data: userId } = await supabase.auth.getUser();
    if (!userId.user?.id) throw new Error('User not authenticated');

    const { data, error } = await supabase.rpc('book_class_slot', {
      in_class_id: params.classId,
      in_user_id: userId.user.id,
      in_class_date: params.classDate,
      in_time: params.time,
      in_duration: params.duration,
      in_gym_id: gymId,
    });

    if (error) {
      const businessCode = findBookingBusinessErrorCode(error.message);
      if (businessCode) throwBookingBusinessError(businessCode);
      throw error;
    }
    return data;
  }
}

/**
 * Check booking eligibility (active subscription covering class date with capacity)
 * for a list of users on a given class date.
 */
export async function checkBookingEligibility(
  userIds: string[],
  classDate: string,
  gymId: string
): Promise<{ eligible: string[]; ineligible: string[] }> {
  if (!userIds.length) return { eligible: [], ineligible: [] };
  const { data, error, authExpired } = await invokeWithRetry('bookings-management', {
    action: 'check_booking_eligibility',
    gym_id: gymId,
    user_ids: userIds,
    class_date: classDate,
  });
  // Pas de *fail-open* : renvoyer « tout le monde est éligible » faisait
  // disparaître les badges « abo expiré » et laissait l'admin croire que la
  // vérification avait eu lieu (audit lot 7). On lève, l'appelant décide quoi
  // afficher — un état « vérification indisponible » est honnête, un badge
  // manquant ne l'est pas.
  if (authExpired) throwAuthExpiredError('check_booking_eligibility');
  if (error) throw error;
  return {
    eligible: data?.eligible || [],
    ineligible: data?.ineligible || [],
  };
}

/**
 * Get all class availabilities for a specific date
 */
export async function getAllClassAvailabilities(
  date: string,
  gymId: string
): Promise<ClassAvailability[]> {
  if (FEATURE_FLAGS.GET_ALL_CLASS_AVAILABILITIES) {
    // Use edge function
    const { data, error, authExpired } = await invokeWithRetry('bookings-management', {
      action: 'get_all_class_availabilities',
      gym_id: gymId,
      date,
    });

    if (authExpired) {
      return [];
    }

    if (error) throw error;
    return data.availabilities;
  } else {
    // Fallback to direct RPC call
    const { data, error } = await supabase.rpc('get_all_class_availabilities', {
      p_date: date,
      p_gym_id: gymId,
    });

    if (error) throw error;
    return data || [];
  }
}
